import { EmptyState, Loader } from '@airship-platform/jam-web/components';
import { useEffect } from 'react';
import { Outlet, useParams } from 'react-router';
import { useAppointmentEquipment } from '../../data-access/appointmentEquipment';
import { AppointmentProposalFilesProvider } from '../../data-access/appointmentProposalFiles';
import { useAppointments } from '../../data-access/appointments';
import { JobImagesProvider } from '../../data-access/jobImages';
import { useServiceHistory } from '../../data-access/serviceHistory';

const AppointmentPage = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();

  const { currentAppointment, isLoading, isLoaded, error, fetchAppointment } =
    useAppointments();
  const { fetchServiceHistory } = useServiceHistory();
  const { fetchAppointmentEquipment } = useAppointmentEquipment();

  useEffect(() => {
    if (appointmentId) {
      const abortController = new AbortController();

      fetchAppointment(appointmentId, {
        signal: abortController.signal,
        callback: (appointment) =>
          Promise.all([
            fetchServiceHistory(
              appointment.appointmentId,
              appointment.location.locationId,
              appointment.customer.customerId,
              { signal: abortController.signal }
            ),
            fetchAppointmentEquipment(appointment.location.locationId, {
              signal: abortController.signal,
            }),
          ]),
      });

      return () => {
        !abortController.signal.aborted &&
          abortController.abort('AppointmentPage unmount');
      };
    }
  }, [
    appointmentId,
    fetchAppointment,
    fetchAppointmentEquipment,
    fetchServiceHistory,
  ]);

  const isAppointmentRouteMatch = !!(
    error ||
    (currentAppointment && currentAppointment.appointmentId === appointmentId)
  );

  if ((isLoading && !isLoaded) || !isAppointmentRouteMatch) {
    return <Loader isLoading message="Loading Appointment" />;
  }

  if (!appointmentId || !currentAppointment) {
    return (
      <EmptyState
        title="Appointment not found"
        subtitle="We couldn't find the appointment you were looking for. Please check the URL or try again later."
      />
    );
  }

  return (
    <AppointmentProposalFilesProvider
      customerId={currentAppointment.customer.customerId}
      appointmentId={currentAppointment.appointmentId}
    >
      <JobImagesProvider jobId={currentAppointment.job.jobId}>
        <Outlet />
      </JobImagesProvider>
    </AppointmentProposalFilesProvider>
  );
};

export default AppointmentPage;
